import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  GoabBadge,
  GoabBlock,
  GoabContainer,
  GoabSpacer,
  GoabModal,
  GoabButton,
  GoabButtonGroup,
  GoabCallout,
  GoabText,
  GoabDetails,
} from "@abgov/react-components";
import DisplayContent from "../../MPP-Common-SPA/components/DynamicForm/Forms/DisplayContent";
import { selectCurrentUser } from "../../Store/api/auth/authSelectors";
import { useSessionManagement } from "../../ServiceUtils/useSessionManagement";
import {
  InternalApplicationStatuses,
  VerificationStatuses,
  StatusTransitionEventEnum,
  VerificationStatusChangeEvent,
} from "../../MPP-Common-SPA/common/constants/statuses";
import {
  domainKey,
  refCategories,
  getBadgeType,
  getBadgeEmphasis,
  formatDate,
  getFieldMappingValue,
  EventsEnabled,
  isPortalActive,
  isEventTypeActive,
} from "../../MPP-Common-SPA/utils/globalUtility";
import {
  useGetFieldMappingQuery,
  useGetProgramFeatureMappingByNameQuery,
} from "../../Store/api/apiSlice";

import "./applicationComponent.css";
import { PAYMENT_VALUES } from "../../MPP-Common-SPA/common/constants/values";
import { FeatureNames } from "../../MPP-Common-SPA/featureFlags/features";
import StatusCalloutWidget from "../../MPP-Common-SPA/Widgets/StatusCallout/StatusCalloutWidget";
import { useProgramSettingsInfo } from "../../PortalRunner/hooks/useProgramSettingsInfo";
import { SettingNames } from "../../MPP-Common-SPA/programSettings/enums";
import { useAdditionalDetails } from "../../hooks/useAdditionalInformation";

const ApplicationComponent = ({
  program,
  application,
  programEvents,
  handleWithdrawApplication,
}) => {
  const {
    id: applicationId,
    internalStatus,
    applicationType,
    uniqueApplicationCode,
    updatedTime,
    applicationDataJson,
    followUpInstruction,
    verificationStatusChangeEvent,
    userDataChanged,
    statusTransitionEvent,
  } = application ?? {};

  const navigate = useNavigate();
  const { getResourceValue, getRefData, clickHandlers } =
    useSessionManagement();
  let user = useSelector(selectCurrentUser);
  let { voucher, benefits } = useAdditionalDetails(
    internalStatus,
    applicationId,
  );
  const applicationData = JSON.parse(applicationDataJson ?? "{}");
  const { data: programEventFeatureMapping } =
    useGetProgramFeatureMappingByNameQuery(
      { featureName: FeatureNames.Events, programId: program?.id },
      { skip: !program?.id },
    );

  const { data: fieldMapping } = useGetFieldMappingQuery();

  const paymentPath = JSON.parse(fieldMapping ?? "{}")?.receive_payment;
  const receivePayment = getFieldMappingValue(
    applicationData?.applicationData?.stepData,
    paymentPath,
  );

  const [
    showWithdrawApplicationConfirmation,
    setShowWithdrawApplicationConfirmation,
  ] = useState(false);

  const handleApplicationClick = (applicationCode) => {
    navigate(`/application?code=${applicationCode}`);
  };

  const selectedEvent = programEvents?.find(
    (evt) => evt.code === applicationType,
  );
  const isActivePortal = isPortalActive(program, user);
  const isActiveEventType = isEventTypeActive(
    programEventFeatureMapping,
    selectedEvent,
    user,
  );

  const isEventsEnabled = EventsEnabled(programEventFeatureMapping);

  const canEdit =
    (isActivePortal && !isEventsEnabled) ||
    (isActiveEventType &&
      (internalStatus === InternalApplicationStatuses.FollowUp ||
        internalStatus === InternalApplicationStatuses.Submitted));

  const appStatusesRefData = getRefData({
    category: refCategories.APP_STATUS,
    domainKey,
  });
  const applicationStatus = appStatusesRefData?.find(
    (e) => e.code === "" + internalStatus,
  )?.description;

  const closeWithdrawApplicationConfirmation = () => {
    setShowWithdrawApplicationConfirmation(false);
    handleWithdrawApplication(applicationId);
  };

  const convertUTCToMountainTime = (utcDateStr) => {
    const date = new Date(utcDateStr + "Z");

    return formatDate(date);
  };

  const isVerifiedStatusChange =
    verificationStatusChangeEvent ===
      VerificationStatusChangeEvent.BasicToPending ||
    verificationStatusChangeEvent ===
      VerificationStatusChangeEvent.BasicToVerified;

  const isEditableStatus =
    internalStatus === InternalApplicationStatuses.Draft ||
    internalStatus === InternalApplicationStatuses.Submitted ||
    internalStatus === InternalApplicationStatuses.FollowUp;

  const showUserDataChangedCallouts = userDataChanged && isEditableStatus;

  const statusCalloutsSetting = useProgramSettingsInfo(
    SettingNames.StatusCallouts,
  );
  const supportInformation = useProgramSettingsInfo(
    SettingNames.PublicNotificationsSupportInformation,
  );

  const configuredCallouts = statusCalloutsSetting?.statusCallouts ?? [];

  const myApplicationsCallouts = configuredCallouts.filter(
    (callout) => callout.isVisibleOnMyApplicationsPage,
  );

  return (
    <>
      <GoabContainer mt="none" mb="none">
        {/* Padding div needed as padding prop is currently bugged in design system for mobile view for GoabContainer (does nothing) */}
        <div className="parent-container">
          <StatusCalloutWidget
            context={{
              internalStatus,
              application,
              statusCallouts: myApplicationsCallouts,
              supportInfo: supportInformation,
              voucher,
              benefits,
            }}
          />
          {showUserDataChangedCallouts &&
            internalStatus === InternalApplicationStatuses.Draft &&
            statusTransitionEvent !==
              StatusTransitionEventEnum.SubmittedToDraft && (
              <GoabCallout
                type="information"
                emphasis="low"
                heading="Your Alberta.ca Account information has changed"
              >
                Please review your application details to make sure the
                information is accurate.
              </GoabCallout>
            )}

          {showUserDataChangedCallouts &&
            (statusTransitionEvent ===
              StatusTransitionEventEnum.SubmittedToDraft ||
              internalStatus === InternalApplicationStatuses.FollowUp) && (
              <GoabCallout type="important" emphasis="low" heading="Resubmit your application">
                Your application has been updated with changes from your
                Alberta.ca Account. Please review the application details and
                resubmit your application.
              </GoabCallout>
            )}
          {isEventsEnabled && !isActiveEventType && (
            <GoabCallout
              type="information"
              emphasis="low"
              heading={getResourceValue({
                key: "RESOURCES@callout.inactiveEvent.header",
                params: { eventName: selectedEvent?.name ?? applicationType },
              })}
              size="large"
            >
              <DisplayContent
                content={getResourceValue({
                  key: "RESOURCES@callout.inactiveEvent.content",
                })}
              />
            </GoabCallout>
          )}
          {/* displays the account verification callout */}
          {(isActiveEventType || !isEventsEnabled) &&
            user.verificationStatus === VerificationStatuses.Basic &&
            internalStatus === InternalApplicationStatuses.FollowUp &&
            !followUpInstruction && (
              <GoabCallout
                type="important"
                emphasis="low"
                heading={getResourceValue({
                  key: "RESOURCES@callout.basicAccountFollowUp.header",
                })}
                size="large"
              >
                <DisplayContent
                  content={getResourceValue({
                    key: "RESOURCES@callout.basicAccountFollowUp.content",
                    params: {
                      url: getResourceValue({
                        key: "RESOURCES@clickhandler.verify_account",
                      }),
                      url2: getResourceValue({
                        key: "RESOURCES@url.alberta_supports",
                      }),
                    },
                  })}
                  clickHandlers={{
                    verifyAccount: () => clickHandlers.verifyAccount(),
                  }}
                />
              </GoabCallout>
            )}
          {/* account status changed - update payment details */}
          {(isActiveEventType || !isEventsEnabled) &&
            isVerifiedStatusChange &&
            (internalStatus === InternalApplicationStatuses.Draft ||
              internalStatus === InternalApplicationStatuses.FollowUp) &&
            !!applicationData?.applicationData?.inReviewMode && (
              <GoabCallout
                type="important"
                emphasis="low"
                heading={getResourceValue({
                  key:
                    internalStatus === InternalApplicationStatuses.Draft
                      ? "RESOURCES@callout.verificationStatusDraftUpdated.header"
                      : "RESOURCES@callout.verificationStatusUpdated.header",
                })}
                size="large"
              >
                <DisplayContent
                  content={getResourceValue({
                    key:
                      internalStatus === InternalApplicationStatuses.Draft
                        ? "RESOURCES@callout.verificationStatusDraftUpdated.content"
                        : "RESOURCES@callout.verificationStatusUpdated.content",
                  })}
                />
              </GoabCallout>
            )}
          {/* opted to visit support center */}
          {(isActiveEventType || !isEventsEnabled) &&
            !isVerifiedStatusChange &&
            user.verificationStatus !== VerificationStatuses.Basic &&
            internalStatus === InternalApplicationStatuses.FollowUp &&
            receivePayment !== PAYMENT_VALUES.ETransfer &&
            !followUpInstruction && (
              <GoabCallout
                type="important"
                emphasis="low"
                heading={getResourceValue({
                  key: "RESOURCES@callout.verifiedAccountFollowUp.header",
                })}
                size="large"
              >
                <DisplayContent
                  content={getResourceValue({
                    key: "RESOURCES@callout.verifiedAccountFollowUp.content",
                    params: {
                      url: getResourceValue({
                        key: "RESOURCES@url.alberta_supports",
                      }),
                    },
                  })}
                />
              </GoabCallout>
            )}

          {/* followUpInstruction */}
          {(isActiveEventType || !isEventsEnabled) &&
            internalStatus === InternalApplicationStatuses.FollowUp &&
            !!followUpInstruction && (
              <GoabCallout
                type="important"
                emphasis="low"
                heading={getResourceValue({
                  key: "RESOURCES@callout.verifiedAccountFollowUpInstruction.header",
                })}
                size="large"
              >
                <DisplayContent
                  content={getResourceValue({
                    key: "RESOURCES@callout.verifiedAccountFollowUpInstruction.content",
                    params: { followUpInstruction },
                  })}
                />
              </GoabCallout>
            )}
          {/* application status in the tile   */}
          {internalStatus && (
            <div className="application-tile">
              {/* application tile on landing page */}
              <GoabBlock direction="column" alignment="start" width="100%">
                <div className="status-row">
                  <GoabBadge
                    type={getBadgeType(internalStatus)}
                    content={getResourceValue({
                      key: "RESOURCES@global.applicationStatus",
                      params: { applicationStatus: applicationStatus },
                    })}
                    emphasis={getBadgeEmphasis(internalStatus)}
                    mt="3xs"
                  ></GoabBadge>
                  <GoabText mb="none" mt="none">
                    {convertUTCToMountainTime(updatedTime)}
                  </GoabText>
                </div>
                <h3>{selectedEvent?.name ?? applicationType}</h3>
                <DisplayContent
                  content={getResourceValue({
                    key: "RESOURCES@global.applicationCode",
                    params: { uniqueApplicationCode },
                  })}
                />
              </GoabBlock>
              <div className="actions">
                <GoabButton
                  type={
                    canEdit ||
                    ((isActiveEventType || !isEventsEnabled) &&
                      internalStatus === InternalApplicationStatuses.Draft)
                      ? "primary"
                      : "secondary"
                  }
                  size="compact"
                  onClick={() => handleApplicationClick(uniqueApplicationCode)}
                  width="100%"
                >
                  {(isActiveEventType || !isEventsEnabled) &&
                  internalStatus === InternalApplicationStatuses.Draft
                    ? getResourceValue({
                        key: "RESOURCES@button.continueApplication",
                      })
                    : canEdit
                      ? getResourceValue({
                          key: "RESOURCES@button.manageApplication",
                        })
                      : getResourceValue({
                          key: "RESOURCES@button.viewApplication",
                        })}
                </GoabButton>
                {(isActiveEventType || !isEventsEnabled) &&
                  internalStatus === InternalApplicationStatuses.Draft && (
                    <GoabButton
                      type="tertiary"
                      size="compact"
                      onClick={() =>
                        setShowWithdrawApplicationConfirmation(true)
                      }
                      width="100%"
                    >
                      {getResourceValue({
                        key: "RESOURCES@button.withdrawApplication",
                      })}
                    </GoabButton>
                  )}
              </div>
              {/* Additional information */}
              {voucher && (
                <div className="additional-details">
                  <GoabDetails heading="Voucher details">
                    <GoabText mt="none">
                      <strong>Store/vendor location: </strong>
                      {voucher.storeVendorLocation}
                    </GoabText>
                    <GoabText mt="none" mb="none">
                      {voucher.instructionsForApplicant}
                    </GoabText>
                  </GoabDetails>
                </div>
              )}
              {benefits && benefits.length > 0 && (
                <div className="additional-details">
                  <GoabDetails heading="Benefit details">
                    <ul className="small-squares application-benefit-list">
                      {benefits.map((benefit, index) => (
                        <li key={index}>
                          <GoabText mt="none" mb="none">
                            {benefit.publicName || benefit.label}
                          </GoabText>
                        </li>
                      ))}
                    </ul>

                    <GoabText mb="none">
                      <strong>
                        Total:{" "}
                        {new Intl.NumberFormat("en-CA", {
                          style: "currency",
                          currency: "CAD",
                        }).format(
                          benefits.reduce(
                            (total, benefit) =>
                              total + (Number(benefit.amount) || 0),
                            0,
                          ),
                        )}
                      </strong>
                    </GoabText>
                  </GoabDetails>
                </div>
              )}
            </div>
          )}
          <GoabModal
            calloutVariant="important"
            role="alertdialog"
            maxWidth="480px"
            heading={getResourceValue({
              key: "RESOURCES@withdrawal.confirm.callout",
            })}
            open={showWithdrawApplicationConfirmation}
          >
            <DisplayContent
              content={getResourceValue({
                key: "RESOURCES@withdrawal.confirm.callout.text",
              })}
            />
            <GoabSpacer vSpacing="xl" />
            <GoabButtonGroup alignment="end">
              <GoabButton
                size="compact"
                type="secondary"
                onClick={() => setShowWithdrawApplicationConfirmation(false)}
              >
                {getResourceValue({ key: "RESOURCES@global.button.cancel" })}
              </GoabButton>
              <GoabButton
                size="compact"
                onClick={() => closeWithdrawApplicationConfirmation()}
              >
                {getResourceValue({ key: "RESOURCES@global.button.confirm" })}
              </GoabButton>
            </GoabButtonGroup>
          </GoabModal>
        </div>
      </GoabContainer>
    </>
  );
};

export default ApplicationComponent;
